<template>
  <div
    class="group overflow-hidden rounded-2xl border bg-white shadow-xs transition-all duration-200 hover:shadow-md"
    :class="[
      slide.active === false
        ? 'border-slate-200 bg-slate-50/50 opacity-80'
        : 'border-slate-200/80',
      isCollapsed ? '' : 'ring-2 ring-indigo-500/10 border-indigo-200',
    ]"
  >
    <!-- Card Header Bar -->
    <div
      class="flex cursor-pointer items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3.5 sm:px-5 py-3 transition hover:bg-slate-100/80 select-none"
      @click="toggleCollapse"
    >
      <div class="flex items-center space-x-2 sm:space-x-3 min-w-0 pr-2">
        <!-- Drag Handle -->
        <span
          class="cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing p-0.5 flex-shrink-0"
          @click.stop
        >
          <GripVerticalIcon class="h-4 w-4 sm:h-5 sm:w-5" />
        </span>

        <!-- Slide Index & Status Badge -->
        <div class="flex items-center space-x-2 flex-shrink-0">
          <span class="text-xs sm:text-sm font-bold text-slate-800"
            >Slide #{{ index + 1 }}</span
          >
          <span
            class="rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold"
            :class="
              slide.active !== false
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-slate-200 text-slate-600'
            "
          >
            {{ slide.active !== false ? "Active" : "Inactive" }}
          </span>
        </div>

        <!-- Slide Title Highlight -->
        <span
          v-if="slide.title"
          class="hidden max-w-[180px] md:max-w-[280px] truncate text-xs text-slate-400 sm:inline"
        >
          — {{ slide.title }}
        </span>
      </div>

      <!-- Header Action Controls -->
      <div
        class="flex items-center space-x-0.5 sm:space-x-1 flex-shrink-0"
        @click.stop
      >
        <button
          type="button"
          title="Move Up"
          :disabled="index === 0"
          class="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-indigo-600 disabled:opacity-25 active:scale-90 transition"
          @click="emit('move-up')"
        >
          <ArrowUpIcon class="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </button>
        <button
          type="button"
          title="Move Down"
          :disabled="index === total - 1"
          class="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-indigo-600 disabled:opacity-25 active:scale-90 transition"
          @click="emit('move-down')"
        >
          <ArrowDownIcon class="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </button>
        <button
          type="button"
          title="Duplicate Slide"
          class="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-indigo-600 active:scale-90 transition"
          @click="emit('duplicate')"
        >
          <CopyIcon class="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </button>
        <button
          type="button"
          title="Delete Slide"
          class="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 active:scale-90 transition"
          @click="emit('delete')"
        >
          <Trash2Icon class="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </button>
        <div class="ml-1 sm:ml-2 h-4 w-px bg-slate-200"></div>
        <button
          type="button"
          class="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-slate-700 active:scale-90 transition"
          @click="toggleCollapse"
        >
          <ChevronDownIcon
            class="h-4 w-4 transition-transform duration-200"
            :class="{ 'rotate-180': !isCollapsed }"
          />
        </button>
      </div>
    </div>

    <!-- Collapsible Form Body -->
    <div
      v-show="!isCollapsed"
      class="space-y-5 sm:space-y-6 p-4 sm:p-6"
    >
      <!-- Image Upload Grid -->
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ImageUploader
          v-model="slide.image"
          label="Desktop Image"
          :required="true"
          :error="errors[`heroSlides.${index}.image`]"
        />
        <ImageUploader
          v-model="slide.mobileImage"
          label="Mobile Image (Optional)"
        />
      </div>

      <!-- Badge, Title, Caption -->
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label
            class="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
            >Badge Text</label
          >
          <input
            v-model="slide.badge"
            type="text"
            placeholder="e.g. ADMISSIONS OPEN 2026-27"
            class="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-800 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
        </div>
        <div>
          <label
            class="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
            >Caption / Subtitle</label
          >
          <input
            v-model="slide.caption"
            type="text"
            placeholder="e.g. Empowering Minds, Shaping Futures"
            class="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-800 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          />
        </div>
      </div>

      <!-- Title Input with Character Count -->
      <div>
        <div class="mb-1 flex items-center justify-between">
          <label
            class="block text-xs font-semibold uppercase tracking-wider text-slate-600"
          >
            Slide Title <span class="text-rose-500">*</span>
          </label>
          <span
            class="text-[11px]"
            :class="
              (slide.title || '').length > 60
                ? 'font-bold text-amber-500'
                : 'text-slate-400'
            "
          >
            {{ (slide.title || "").length }}/80
          </span>
        </div>
        <input
          v-model="slide.title"
          type="text"
          maxlength="80"
          placeholder="e.g. Welcome to Educanium International School"
          class="w-full rounded-xl border px-3.5 py-2.5 text-xs sm:text-sm font-semibold text-slate-800 transition focus:outline-none focus:ring-2"
          :class="
            errors[`heroSlides.${index}.title`]
              ? 'border-rose-300 bg-rose-50/20 focus:border-rose-500 focus:ring-rose-500/20'
              : 'border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20'
          "
        />
        <p
          v-if="errors[`heroSlides.${index}.title`]"
          class="mt-1 text-xs font-medium text-rose-500"
        >
          {{ errors[`heroSlides.${index}.title`] }}
        </p>
      </div>

      <!-- Description Textarea -->
      <div>
        <label
          class="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
          >Description</label
        >
        <textarea
          v-model="slide.description"
          rows="3"
          placeholder="Brief description highlighting key achievements, admissions, or values..."
          class="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-800 transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        ></textarea>
      </div>

      <!-- Call-To-Action Buttons Card -->
      <div
        class="space-y-4 rounded-xl border border-slate-200/80 bg-slate-50/50 p-3.5 sm:p-4"
      >
        <h4 class="text-xs font-bold uppercase tracking-wider text-slate-500">
          Call-to-Action Buttons
        </h4>

        <!-- Primary Button Group -->
        <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <label class="mb-1 block text-[11px] font-medium text-slate-600"
              >Primary Button Text</label
            >
            <input
              v-model="slide.buttonText"
              type="text"
              placeholder="e.g. Apply Now"
              class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div>
            <label class="mb-1 block text-[11px] font-medium text-slate-600"
              >Primary Button Link</label
            >
            <input
              v-model="slide.buttonLink"
              type="text"
              placeholder="e.g. /admissions or https://..."
              class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div>
            <label class="mb-1 block text-[11px] font-medium text-slate-600"
              >Target</label
            >
            <select
              v-model="slide.buttonTarget"
              class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
            >
              <option value="_self">Same Window (_self)</option>
              <option value="_blank">New Tab (_blank)</option>
            </select>
          </div>
        </div>

        <!-- Secondary Button Group -->
        <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <label class="mb-1 block text-[11px] font-medium text-slate-600"
              >Secondary Button Text</label
            >
            <input
              v-model="slide.secondaryButtonText"
              type="text"
              placeholder="e.g. Virtual Tour"
              class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div>
            <label class="mb-1 block text-[11px] font-medium text-slate-600"
              >Secondary Button Link</label
            >
            <input
              v-model="slide.secondaryButtonLink"
              type="text"
              placeholder="e.g. /tour"
              class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div>
            <label class="mb-1 block text-[11px] font-medium text-slate-600"
              >Target</label
            >
            <select
              v-model="slide.secondaryButtonTarget"
              class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none"
            >
              <option value="_self">Same Window (_self)</option>
              <option value="_blank">New Tab (_blank)</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Overlay & Content Alignment & Active Toggle -->
      <div class="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-3">
        <!-- Overlay Darkness Slider -->
        <div>
          <div class="mb-2 flex items-center justify-between">
            <label
              class="text-xs font-semibold uppercase tracking-wider text-slate-600"
              >Overlay Darkness</label
            >
            <span
              class="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs font-bold text-slate-700"
            >
              {{ Math.round((slide.overlayOpacity || 0.4) * 100) }}%
            </span>
          </div>
          <input
            v-model.number="slide.overlayOpacity"
            type="range"
            min="0"
            max="1"
            step="0.05"
            class="h-2 w-full cursor-pointer rounded-lg bg-slate-200 accent-indigo-600"
          />
        </div>

        <!-- Text Alignment Picker -->
        <div>
          <label
            class="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-600"
            >Text Alignment</label
          >
          <div class="flex items-center space-x-1 rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              class="flex flex-1 items-center justify-center rounded-lg py-1.5 text-xs font-semibold transition"
              :class="
                slide.contentAlignment === 'left'
                  ? 'bg-white text-indigo-600 shadow-xs'
                  : 'text-slate-500 hover:text-slate-800'
              "
              @click="slide.contentAlignment = 'left'"
            >
              <AlignLeftIcon class="mr-1 h-3.5 w-3.5" /> Left
            </button>
            <button
              type="button"
              class="flex flex-1 items-center justify-center rounded-lg py-1.5 text-xs font-semibold transition"
              :class="
                slide.contentAlignment === 'center'
                  ? 'bg-white text-indigo-600 shadow-xs'
                  : 'text-slate-500 hover:text-slate-800'
              "
              @click="slide.contentAlignment = 'center'"
            >
              <AlignCenterIcon class="mr-1 h-3.5 w-3.5" /> Center
            </button>
            <button
              type="button"
              class="flex flex-1 items-center justify-center rounded-lg py-1.5 text-xs font-semibold transition"
              :class="
                slide.contentAlignment === 'right'
                  ? 'bg-white text-indigo-600 shadow-xs'
                  : 'text-slate-500 hover:text-slate-800'
              "
              @click="slide.contentAlignment = 'right'"
            >
              <AlignRightIcon class="mr-1 h-3.5 w-3.5" /> Right
            </button>
          </div>
        </div>

        <!-- Sort Order & Active Switch -->
        <div class="flex items-center justify-between space-x-4">
          <div class="flex-1">
            <label
              class="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >Sort Order</label
            >
            <input
              v-model.number="slide.sortOrder"
              type="number"
              class="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-800 focus:border-indigo-500 focus:outline-none"
            />
          </div>
          <div class="flex flex-col items-end">
            <label
              class="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-600"
              >Slide Active</label
            >
            <button
              type="button"
              class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
              :class="slide.active !== false ? 'bg-indigo-600' : 'bg-slate-300'"
              @click="slide.active = slide.active === false"
            >
              <span
                class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                :class="
                  slide.active !== false ? 'translate-x-5' : 'translate-x-0'
                "
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref } from "vue";
  import {
    GripVertical as GripVerticalIcon,
    ArrowUp as ArrowUpIcon,
    ArrowDown as ArrowDownIcon,
    Copy as CopyIcon,
    Trash2 as Trash2Icon,
    ChevronDown as ChevronDownIcon,
    AlignLeft as AlignLeftIcon,
    AlignCenter as AlignCenterIcon,
    AlignRight as AlignRightIcon,
  } from "lucide-vue-next";
  import ImageUploader from "@/components/WEB/ImageUploader.vue";

  defineProps({
    slide: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
  });

  const emit = defineEmits(["move-up", "move-down", "duplicate", "delete"]);

  const isCollapsed = ref(false);

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value;
  };
</script>
